import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, MailCheck, Plane, Inbox, Users, Send, RefreshCw, CalendarClock, AlertCircle } from "lucide-react";

const PERIODS = [
  { value: "0", label: "Histórico completo" },
  { value: "7", label: "Últimos 7 días" },
  { value: "30", label: "Últimos 30 días" },
  { value: "90", label: "Últimos 90 días" },
];

const TYPE_LABELS = {
  hotel: "Hoteles", transfer: "Transfers", activity: "Actividades", actividad: "Actividades",
  restaurant: "Restaurantes", train: "Trenes", flight: "Vuelos", rental_car: "Coches", other: "Otros",
};
const ROLE_LABELS = { admin: "Administrador", agent: "Agente", reconfirmador: "Reconfirmador" };

const fmtDt = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

function WeeklyCard({ days }) {
  const [settings, setSettings] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/reports/settings");
    setSettings(data);
    setRecipient(data.recipient || "");
  }, []);
  useEffect(() => { load(); }, [load]);

  const onToggle = async (v) => {
    try {
      const { data } = await api.patch("/reports/settings", { weekly_enabled: v });
      setSettings(data);
      toast.success(v ? "Envío semanal activado — lunes 9:00 (Madrid)" : "Envío semanal desactivado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const onSaveRecipient = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/reports/settings", { recipient });
      setSettings(data);
      toast.success("Destinatario guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email no válido");
    } finally {
      setSaving(false);
    }
  };

  const onSendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/usage/send", { days: Number(days) });
      toast.success(`Reporte enviado a ${data.recipient}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el reporte");
    } finally {
      setSending(false);
    }
  };

  if (!settings) return null;
  return (
    <div className="vv-card p-5" data-testid="weekly-report-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
            <CalendarClock size={18} strokeWidth={1.8} />
          </div>
          <div>
            <h3 className="text-sm font-semibold">Envío semanal por correo</h3>
            <p className="text-xs text-gray-500 mt-0.5">Cada lunes a las 9:00 (hora Madrid) se envía el reporte de los últimos 7 días desde la cuenta del administrador.</p>
            <div className="text-[11px] text-gray-400 mt-1.5" data-testid="weekly-last-sent">
              {settings.last_sent_at ? `Último envío: ${fmtDt(settings.last_sent_at)}` : "Aún no se ha enviado ningún reporte."}
            </div>
            {settings.last_error && (
              <div className="text-[11px] text-red-600 mt-1 flex items-center gap-1" data-testid="weekly-last-error">
                <AlertCircle size={12} /> {settings.last_error}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{settings.weekly_enabled ? "Activado" : "Desactivado"}</span>
          <Switch checked={settings.weekly_enabled} onCheckedChange={onToggle} data-testid="weekly-report-switch" />
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mt-4">
        <div className="flex-1 min-w-[220px] max-w-sm">
          <label className="vv-label">Destinatario</label>
          <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="mt-1" data-testid="weekly-recipient-input" />
        </div>
        <Button variant="outline" size="sm" onClick={onSaveRecipient}
          disabled={saving || !recipient || recipient === settings.recipient} data-testid="weekly-save-recipient">
          {saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
          Guardar
        </Button>
        <Button size="sm" onClick={onSendNow} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="weekly-send-now">
          {sending ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />}
          Enviar ahora ({PERIODS.find((p) => p.value === days)?.label?.toLowerCase()})
        </Button>
      </div>
    </div>
  );
}

function TypeChips({ byType }) {
  const entries = Object.entries(byType || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
          {TYPE_LABELS[k] || k} <strong>{v}</strong>
        </span>
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const [days, setDays] = useState("0");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/usage?days=${d}`);
      setReport(data);
    } catch (e) {
      toast.error("Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const t = report?.totals || {};
  const summary = [
    { key: "emails_total", label: "Correos enviados", icon: MailCheck, color: "text-emerald-700 bg-emerald-50", sub: `${t.emails_real ?? 0} reales · ${t.emails_simulated ?? 0} simulados · ${t.emails_failed ?? 0} fallidos` },
    { key: "trips_imported", label: "Viajes importados", icon: Plane, color: "text-blue-700 bg-blue-50" },
    { key: "replies_managed", label: "Respuestas gestionadas", icon: Inbox, color: "text-violet-700 bg-violet-50", sub: `${t.replies_pending ?? 0} pendientes ahora` },
    { key: "active_users", label: "Usuarios activos", icon: Users, color: "text-orange-700 bg-orange-50" },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="vv-label">Administración</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Reporte de uso</h1>
          <p className="text-sm text-gray-500 mt-1">Actividad por agente: correos, viajes, respuestas y accesos.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[190px]" data-testid="report-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => load(days)} title="Refrescar" data-testid="report-refresh">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {summary.map((c) => (
          <div key={c.key} className="vv-card p-4" data-testid={`report-stat-${c.key}`}>
            <div className={`w-9 h-9 rounded-md flex items-center justify-center ${c.color}`}>
              <c.icon size={18} strokeWidth={1.8} />
            </div>
            <div className="text-2xl font-semibold mt-3 tracking-tight">{loading ? "…" : (t[c.key] ?? "—")}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
            {c.sub && !loading && <div className="text-[10px] text-gray-400 mt-0.5">{c.sub}</div>}
          </div>
        ))}
      </div>

      <div className="vv-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--vv-border)]">
          <h3 className="text-sm font-semibold">Actividad por usuario</h3>
          <p className="text-xs text-gray-500">Qué hace cada agente y con qué frecuencia. Los accesos se contabilizan desde junio 2026.</p>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-400">Cargando…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="vv-table w-full" data-testid="report-users-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Último acceso</th>
                  <th className="text-center">Accesos</th>
                  <th className="text-center">Correos</th>
                  <th>Por tipo de servicio</th>
                  <th className="text-center">Viajes importados</th>
                  <th>Respuestas gestionadas</th>
                </tr>
              </thead>
              <tbody>
                {(report?.users || []).map((r) => (
                  <tr key={r.id} data-testid={`report-user-row-${r.email}`}>
                    <td>
                      <div className="font-medium text-gray-800">{r.name}</div>
                      <div className="text-[11px] text-gray-500">{r.email} · {ROLE_LABELS[r.role] || r.role}</div>
                    </td>
                    <td className="text-xs text-gray-600">{fmtDt(r.last_login_at)}</td>
                    <td className="text-center">{r.logins}</td>
                    <td className="text-center">
                      <div className="font-medium">{r.emails.total}</div>
                      <div className="text-[10px] text-gray-400">
                        {r.emails.real} reales{r.emails.simulated ? ` · ${r.emails.simulated} sim` : ""}{r.emails.failed ? ` · ${r.emails.failed} fallidos` : ""}{r.emails.queued ? ` · ${r.emails.queued} en cola` : ""}
                      </div>
                    </td>
                    <td><TypeChips byType={r.by_type} /></td>
                    <td className="text-center">{r.trips_imported}</td>
                    <td className="text-xs text-gray-600">
                      {r.replies.applied + r.replies.dismissed + r.replies.responded === 0 ? (
                        <span className="text-gray-300">—</span>
                      ) : (
                        <>{r.replies.applied} aplicadas · {r.replies.dismissed} descartadas · {r.replies.responded} respondidas</>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <WeeklyCard days={days} />
    </div>
  );
}
